"use client"

import * as React from "react"

import { LeoIcon } from "@/components/ui/leo-icon"
import { performanceIssueLabel } from "@/lib/question-quality"
import type {
  QuestionQualityAlternate,
  QuestionQualityCheck,
  QuestionQualityHistory,
} from "@/lib/question-quality/types"
import { cn } from "@/lib/utils"

import { useLeoApplyMotion } from "@/components/question-quality/leo-apply-motion"

type ChipTrailMode = "arrow-hover-leo"

function ChipTrailIcon({
  mode,
  applying,
  applyingMotion,
  revealed,
}: {
  mode: ChipTrailMode
  applying: boolean
  applyingMotion: "working" | "answered" | null
  revealed: boolean
}) {
  const showLeo = applying || revealed

  return (
    <span className="relative flex size-3.5 shrink-0 items-center justify-center">
      {showLeo ? (
        <LeoIcon
          variant="ambient"
          size="xs"
          state={
            applying && applyingMotion
              ? applyingMotion
              : revealed
                ? "invited"
                : "rest"
          }
          className="size-3.5"
        />
      ) : (
        <i
          className="fa-light fa-arrow-right text-[0.7rem] text-muted-foreground transition-colors group-hover:text-brand"
          aria-hidden="true"
        />
      )}
    </span>
  )
}

export const QUESTION_QUALITY_SUGGESTION_CHIP_CLASS =
  "group flex w-full cursor-pointer items-center gap-2.5 rounded-xl border border-border/80 bg-card/55 px-3 py-2.5 text-start text-[0.8125rem] leading-snug shadow-xs backdrop-blur-md transition-[border-color,box-shadow,background-color] hover:border-brand/35 hover:bg-interactive-hover/80 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"

export const QUESTION_QUALITY_SUGGESTION_TWIN_SHELL_CLASS =
  "overflow-hidden rounded-2 border border-border/80 bg-card/75 shadow-xs"

export function QuestionQualitySuggestionTwinChip({
  label,
  meta,
  applyKey,
  trailMode = "arrow-hover-leo",
  disabled,
  onUse,
}: {
  label: string
  meta?: string | null
  applyKey: string
  trailMode?: ChipTrailMode
  disabled?: boolean
  onUse: () => void
}) {
  return (
    <section aria-label={label} className={QUESTION_QUALITY_SUGGESTION_TWIN_SHELL_CLASS}>
      <QuestionQualitySuggestionChip
        label={label}
        meta={meta}
        applyKey={applyKey}
        trailMode={trailMode}
        disabled={disabled}
        onUse={onUse}
        embedded
      />
    </section>
  )
}

export function QuestionQualitySuggestionChip({
  label,
  meta,
  applyKey,
  trailMode = "arrow-hover-leo",
  disabled,
  onUse,
  embedded = false,
}: {
  label: string
  meta?: string | null
  applyKey: string
  trailMode?: ChipTrailMode
  disabled?: boolean
  onUse: () => void
  embedded?: boolean
}) {
  const { captureSource, applyingKey, applyingMotion } = useLeoApplyMotion()
  const applying = applyingKey === applyKey
  const [revealed, setRevealed] = React.useState(false)

  return (
    <button
      type="button"
      disabled={disabled}
      aria-busy={applying && applyingMotion === "working" ? true : undefined}
      aria-label={meta ? `${label}. ${meta}` : label}
      onMouseEnter={() => setRevealed(true)}
      onMouseLeave={() => setRevealed(false)}
      onFocus={() => setRevealed(true)}
      onBlur={() => setRevealed(false)}
      onClick={(event) => {
        captureSource(event.currentTarget, applyKey)
        onUse()
      }}
      className={cn(
        QUESTION_QUALITY_SUGGESTION_CHIP_CLASS,
        embedded &&
          "rounded-none border-0 bg-transparent shadow-none backdrop-blur-none hover:shadow-none",
      )}
    >
      <span className="flex min-w-0 flex-1 items-baseline gap-2">
        <span className="min-w-0">{label}</span>
        {meta ? (
          <span
            className={cn(
              "shrink-0 font-mono text-xs tabular-nums text-muted-foreground",
              meta.includes("×") && "font-semibold text-destructive",
            )}
          >
            {meta}
          </span>
        ) : null}
      </span>
      <ChipTrailIcon
        mode={trailMode}
        applying={applying && Boolean(applyingMotion)}
        applyingMotion={applyingMotion}
        revealed={revealed}
      />
    </button>
  )
}

export function QuestionQualitySuggestionChips({
  check,
  history = null,
  recs,
  disabled,
  onUse,
  twin = false,
  showIssueMeta,
}: {
  check: QuestionQualityCheck
  history?: QuestionQualityHistory | null
  recs: QuestionQualityAlternate[]
  disabled?: boolean
  onUse: (alternate: QuestionQualityAlternate) => void
  /** Each chip is its own sibling card, not nested in a recommendation shell. */
  twin?: boolean
  /** When false, the issue lives in the pager header; chips are actions only. */
  showIssueMeta?: boolean
}) {
  if (recs.length === 0) return null

  const metaOnChips = showIssueMeta ?? !twin
  const meta =
    metaOnChips && history?.sat ? performanceIssueLabel(check, history) : null

  return (
    <ul
      className={cn(
        "m-0 flex list-none flex-col gap-2 p-0",
        twin && "gap-2.5",
      )}
      aria-label="Suggested options"
    >
      {recs.map((alternate) => (
          <li key={alternate.id} className="list-none">
            {twin ? (
              <QuestionQualitySuggestionTwinChip
                label={alternate.actionLabel}
                meta={meta}
                applyKey={alternate.id}
                disabled={disabled}
                onUse={() => onUse(alternate)}
              />
            ) : (
              <QuestionQualitySuggestionChip
                label={alternate.actionLabel}
                meta={meta}
                applyKey={alternate.id}
                disabled={disabled}
                onUse={() => onUse(alternate)}
              />
            )}
          </li>
        ))}
    </ul>
  )
}
